import { Component, escapeHtml, register } from './core.js';
import { iconHtml } from './icons.js';
import { cx, pick, attrs, mountRoot } from './chrome.js';

const VARIANTS = ['primary', 'secondary', 'ghost', 'danger'];
const DEFAULTS = { label: '', icon: null, iconOnly: false, variant: 'secondary', size: 'md', disabled: false, busy: false, type: 'button', ariaLabel: null, onClick: null, action: null };

/** The house spinner: a CSS-drawn ring, hidden from assistive tech (aria-busy carries the state). */
export const SPINNER_HTML = '<span class="dfc-spinner" aria-hidden="true"></span>';

/** Shared markup builder for Button (live and static). Also used by the other primitives for their action buttons. */
export function buttonHtml(opts) {
	const o = Object.assign({}, DEFAULTS, opts);
	const label = o.label === undefined || o.label === null ? '' : String(o.label);
	if (o.iconOnly && !o.ariaLabel) throw new Error('dfc Button: an icon-only button needs an ariaLabel so it has an accessible name.');
	if (!o.iconOnly && !label) throw new Error('dfc Button: label is required (or set iconOnly with an ariaLabel).');
	const busy = !!o.busy;
	const cls = cx('dfc', 'dfc-btn', 'dfc-btn-' + pick(o.variant, VARIANTS, 'secondary'), 'dfc-size-' + pick(o.size, ['sm', 'md'], 'md'), o.iconOnly && 'is-icon-only', busy && 'is-busy', o.className);
	const lead = busy ? SPINNER_HTML : iconHtml(o.icon, 'dfc-btn-icon');
	return `<button${attrs({ type: pick(o.type, ['button', 'submit'], 'button'), class: cls, disabled: !!o.disabled, 'aria-busy': busy ? 'true' : null, 'aria-label': o.ariaLabel || null, title: o.iconOnly ? o.ariaLabel : null, 'data-dfc-action': o.action })}>${lead || ''}${o.iconOnly ? '' : `<span class="dfc-btn-label">${escapeHtml(label)}</span>`}</button>`;
}

/**
 * Button: a native <button> with variant, size, icon, busy and disabled states.
 *
 * new dfc.Button(container, {
 *   label, icon, iconOnly: false, ariaLabel (required when iconOnly),
 *   variant: 'primary' | 'secondary' | 'ghost' | 'danger', size: 'sm' | 'md',
 *   disabled: false, busy: false, type: 'button' | 'submit', onClick
 * })
 * Events: click { event }
 * Static: dfc.Button.renderStatic(opts) -> HTML string
 */
export class Button extends Component {
	static get componentName() { return 'btn'; }
	defaults() { return Object.assign({}, DEFAULTS); }

	render() {
		const el = mountRoot(this, buttonHtml(this.options));
		el.addEventListener('click', (e) => {
			if (this.options.disabled || this.options.busy) { e.preventDefault(); return; }
			this.emit('click', { event: e });
			if (typeof this.options.onClick === 'function') this.options.onClick(e);
		});
	}

	focus() { if (this.el) this.el.focus(); }

	static renderStatic(opts = {}) { return buttonHtml(opts); }
}

register('button', Button);
